import { UserData, QuotaCheckResult, TrackingResult, AnalysisHistory } from '../types';
import { AnalysisHistoryAnalyzeTypeEnum } from '../api/protected-bricks/generated/src';
import { OidcUserProfile } from '../types/oidc';
import { apiService } from './apiService';

class AccountService {
  // LocalStorage keys kept for fallback/caching only
  private readonly STORAGE_KEY = 'userData';
  private readonly HISTORY_KEY = 'analysisHistory';

  /**
   * Erstellt UserData aus OIDC-Profil
   */
  getUserDataFromOidc(oidcProfile: OidcUserProfile | undefined): UserData {
    const plan = oidcProfile?.plan || 'free';
    const monthlyLimit = this.getPlanLimit(plan);

    return {
      name: oidcProfile?.name || oidcProfile?.preferred_username || 'Benutzer',
      email: oidcProfile?.email || '',
      company: oidcProfile?.company || '',
      plan,
      monthlyLimit,
      usedBricks: 0,
      resetDate: this.getNextMonthResetDate(),
    };
  }

  getDefaultUserData(): UserData {
    return {
      name: 'Demo User',
      email: 'demo@rebricker.com',
      company: 'reBricker GmbH',
      plan: 'premium',
      monthlyLimit: 10000,
      usedBricks: 0,
      resetDate: this.getNextMonthResetDate(),
    };
  }

  /**
   * Holt UserData von der API
   */
  async getUserData(): Promise<UserData> {
    try {
      const data = await apiService.getAccount();
      // Cache in localStorage als Fallback
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
      return data;
    } catch (error) {
      console.error('Error loading user data from API:', error);
      // Fallback zu localStorage
      try {
        const stored = localStorage.getItem(this.STORAGE_KEY);
        if (stored) {
          return JSON.parse(stored);
        }
      } catch (storageError) {
        console.error('Error loading from localStorage:', storageError);
      }
      return this.getDefaultUserData();
    }
  }

  /**
   * Synchrone Version für Fallback (deprecated, use getUserData() instead)
   */
  getUserDataSync(): UserData {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
    return this.getDefaultUserData();
  }

  saveUserData(userData: UserData): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(userData));
    } catch (error) {
      console.error('Error saving user data:', error);
    }
  }

  /**
   * Aktualisiert UserData über die API
   */
  async updateUserData(updates: Partial<UserData>): Promise<UserData> {
    try {
      // Wenn usedBricks aktualisiert wird, sende an API
      if (updates.usedBricks !== undefined) {
        const currentData = this.getUserDataSync();
        const bricksAnalyzed = updates.usedBricks - currentData.usedBricks;
        if (bricksAnalyzed > 0) {
          await apiService.updateAccountUsage(bricksAnalyzed, '');
        }
      }
      // Hole aktualisierte Daten von der API
      return await this.getUserData();
    } catch (error) {
      console.error('Error updating user data:', error);
      // Fallback zu localStorage
      const currentData = this.getUserDataSync();
      const updatedData = { ...currentData, ...updates };
      this.saveUserData(updatedData);
      return updatedData;
    }
  }

  canAnalyze(requiredBricks: number): QuotaCheckResult {
    const userData = this.getUserDataSync();
    const remainingBricks = userData.monthlyLimit - userData.usedBricks;

    return {
      allowed: remainingBricks >= requiredBricks,
      requiredBricks,
      remainingBricks,
    };
  }

  /**
   * Fügt eine Analyse hinzu und aktualisiert die Nutzung über die API
   * @param bricksCount Anzahl der analysierten Steine
   * @param setId Set-ID (wird auch als documentId für die API verwendet)
   * @param analyzeType Art der Analyse (SIMPLE, PROTECTED, ALL)
   */
  async addAnalysis(
    bricksCount: number,
    setId: string,
    analyzeType: AnalysisHistoryAnalyzeTypeEnum = AnalysisHistoryAnalyzeTypeEnum.All
  ): Promise<TrackingResult> {
    try {
      // Update usage via API (API erwartet documentId, wir verwenden setId)
      await apiService.updateAccountUsage(bricksCount, setId);

      // Get updated data
      const userData = await this.getUserData();

      return {
        success: true,
        remainingBricks: userData.monthlyLimit - userData.usedBricks,
      };
    } catch (error) {
      console.error('Error adding analysis:', error);
      // Fallback zu localStorage
      const userData = this.getUserDataSync();
      const newUsedBricks = userData.usedBricks + bricksCount;

      this.saveUserData({
        ...userData,
        usedBricks: newUsedBricks,
      });

      this.addToHistory({
        date: new Date().toISOString(),
        bricksAnalyzed: bricksCount,
        setId,
        analyzeType,
      });

      return {
        success: true,
        remainingBricks: userData.monthlyLimit - newUsedBricks,
      };
    }
  }

  /**
   * Holt Analyse-Historie von der API
   */
  async getAnalysisHistory(): Promise<AnalysisHistory[]> {
    try {
      const data = await apiService.getAccountHistory();
      // Cache in localStorage als Fallback
      localStorage.setItem(this.HISTORY_KEY, JSON.stringify(data));
      return data;
    } catch (error) {
      console.error('Error loading analysis history from API:', error);
      // Fallback zu localStorage
      try {
        const stored = localStorage.getItem(this.HISTORY_KEY);
        if (stored) {
          return JSON.parse(stored);
        }
      } catch (storageError) {
        console.error('Error loading from localStorage:', storageError);
      }
      return [];
    }
  }

  /**
   * Synchrone Version für localStorage-Fallback
   */
  private addToHistory(entry: AnalysisHistory): void {
    try {
      const stored = localStorage.getItem(this.HISTORY_KEY);
      const history = stored ? JSON.parse(stored) : [];
      history.unshift(entry); // Add to beginning

      // Keep only last 100 entries
      if (history.length > 100) {
        history.splice(100);
      }

      localStorage.setItem(this.HISTORY_KEY, JSON.stringify(history));
    } catch (error) {
      console.error('Error adding to history:', error);
    }
  }

  async resetMonthlyQuota(): Promise<void> {
    await this.updateUserData({
      usedBricks: 0,
      resetDate: this.getNextMonthResetDate(),
    });
  }

  getNextMonthResetDate(): string {
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return nextMonth.toISOString();
  }

  getUsagePercentage(): number {
    const userData = this.getUserDataSync();
    return (userData.usedBricks / userData.monthlyLimit) * 100;
  }

  getRemainingBricks(): number {
    const userData = this.getUserDataSync();
    return Math.max(0, userData.monthlyLimit - userData.usedBricks);
  }

  getPlanName(plan: UserData['plan']): string {
    const planNames = {
      free: 'Kostenlos',
      // basic: 'Basic',
      premium: 'Premium',
      // enterprise: 'Enterprise',
    };
    return planNames[plan] || plan;
  }

  getPlanLimit(plan: UserData['plan']): number {
    const limits = {
      free: 200,
      // basic: 1000,
      premium: 10000,
      // enterprise: 100000,
    };
    return limits[plan] || 0;
  }
}

export const accountService = new AccountService();
